/**
 * Pending Invitations Section Component
 *
 * Displays and manages pending organization invitations.
 */

import { useCallback, useState } from 'react';
import { Delete, Send } from '@mui/icons-material';
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { getRoleBadgeColor,getRoleDisplayName } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import {
  useDeleteInvitationMutation,
  useResendInvitationMutation,
} from '../store/api';
import { useInvitationStatus } from '../store/hooks';
import type { OrganizationInvitation } from '../types';

interface PendingInvitationsSectionProps {
  invitations: OrganizationInvitation[];
  onInvitationChange: () => void;
}

export function PendingInvitationsSection({
  invitations,
  onInvitationChange,
}: PendingInvitationsSectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [deleteInvitation] = useDeleteInvitationMutation();
  const [resendInvitation] = useResendInvitationMutation();
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});

  const setLoading = useCallback((invitationId: string, loading: boolean) => {
    setLoadingStates(prev => ({
      ...prev,
      [invitationId]: loading,
    }));
  }, []);

  const handleDelete = useCallback(async (invitationId: string) => {
    setLoading(`delete-${invitationId}`, true);
    try {
      await deleteInvitation({ invitationId }).unwrap();
      enqueueSnackbar('Invitation removed successfully', { variant: 'success' });
      onInvitationChange();
    } catch (error: any) {
      enqueueSnackbar(
        error?.data?.message || 'Failed to remove invitation',
        { variant: 'error' }
      );
    } finally {
      setLoading(`delete-${invitationId}`, false);
    }
  }, [deleteInvitation, enqueueSnackbar, onInvitationChange, setLoading]);

  const handleResend = useCallback(async (invitationId: string) => {
    setLoading(`resend-${invitationId}`, true);
    try {
      await resendInvitation({ invitationId }).unwrap();
      enqueueSnackbar('Invitation resent successfully', { variant: 'success' });
      onInvitationChange();
    } catch (error: any) {
      enqueueSnackbar(
        error?.data?.message || 'Failed to resend invitation',
        { variant: 'error' }
      );
    } finally {
      setLoading(`resend-${invitationId}`, false);
    }
  }, [resendInvitation, enqueueSnackbar, onInvitationChange, setLoading]);

  if (invitations.length === 0) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Pending Invitations" />
      <CardContent sx={{ p: 0 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.map((invitation) => (
                <InvitationRow
                  key={invitation.invitationId}
                  invitation={invitation}
                  onDelete={handleDelete}
                  onResend={handleResend}
                  isDeleting={loadingStates[`delete-${invitation.invitationId}`] || false}
                  isResending={loadingStates[`resend-${invitation.invitationId}`] || false}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

interface InvitationRowProps {
  invitation: OrganizationInvitation;
  onDelete: (invitationId: string) => void;
  onResend: (invitationId: string) => void;
  isDeleting: boolean;
  isResending: boolean;
}

function InvitationRow({
  invitation,
  onDelete,
  onResend,
  isDeleting,
  isResending,
}: InvitationRowProps) {
  const status = useInvitationStatus(invitation.expiresAt);
  const isExpired = status === 'expired';

  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2">{invitation.email}</Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={getRoleDisplayName(invitation.role)}
          size="small"
          color={getRoleBadgeColor(invitation.role)}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {new Date(invitation.sentAt).toLocaleDateString()}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={isExpired ? 'Expired' : 'Pending'}
          color={isExpired ? 'error' : 'warning'}
          size="small"
        />
      </TableCell>
      <TableCell align="right">
        {isExpired && (
          <Tooltip title="Resend Invitation">
            <span>
              <IconButton
                size="small"
                onClick={() => onResend(invitation.invitationId)}
                disabled={isResending}
              >
                {isResending ? (
                  <CircularProgress size={18} />
                ) : (
                  <Send fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Remove Invitation">
          <span>
            <IconButton
              size="small"
              onClick={() => onDelete(invitation.invitationId)}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <CircularProgress size={18} />
              ) : (
                <Delete fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}